import {
    MANUAL_QUALITY_REQUEST_TOPIC,
    MANUAL_QUALITY_RESPONSE_TOPIC_PREFIX,
    MQTTEventsEnum,
} from "constants/constants";
import * as mqtt from "precompiled-mqtt";
import { IConnectionSetting } from "../EventReport/EventReportInterfaces";
import { IManualQualityRequestPayload, IManualQualityResponsePayload } from "./ManualQualityInterfaces";

const { connect } = mqtt;

const baseConnectionSettings = {
    clientId: undefined as string | undefined,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
    protocol: "ws" as "ws",
    username: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
};

const defaultBrokerSettings: IConnectionSetting = {
    allow_anonymous: false,
    host: process.env.REACT_APP_MOSQUITTO_BROKER_URL || "mqtt://test.mosquitto.org:8081",
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
    user: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
};

export interface IPublishManualQualityOptions {
    /** Broker overrides (defaults to env-driven settings). */
    broker?: IConnectionSetting;
    /** Optional MQTT clientId to use for this round-trip. */
    clientId?: string;
    /** Maximum time in ms to wait for the local-server response (default 6 s). */
    timeoutMs?: number;
}

/**
 * Publishes a manual scrap-entry submission and waits for the matching
 * response on `manual-quality-response-<mcId>`. Resolves on the first
 * incoming message whose `uuid` matches the request, rejects on timeout
 * or connection failure. The caller is expected to keep the request
 * queued in localStorage and retry with the **same uuid** until success
 * — the local-server idempotency cache short-circuits duplicates.
 */
export const ManualQualityMQTTEntity = {
    publishManualQuality: (
        payload: IManualQualityRequestPayload,
        options: IPublishManualQualityOptions = {},
    ): Promise<IManualQualityResponsePayload> => {
        const broker = options.broker ?? defaultBrokerSettings;
        const timeoutMs = options.timeoutMs ?? 6000;

        return new Promise<IManualQualityResponsePayload>((resolve, reject) => {
            const isWSS = broker.host.startsWith("wss");
            const client = connect(broker.host, {
                ...baseConnectionSettings,
                clean: true,
                clientId: options.clientId,
                password: broker.password,
                protocol: isWSS ? "wss" : "ws",
                username: broker.user,
            });

            const responseTopic = `${MANUAL_QUALITY_RESPONSE_TOPIC_PREFIX}${payload.machineCenterId}`;

            let settled = false;
            const settle = (fn: () => void) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timeoutId);
                try {
                    client.end(true);
                } catch {
                    // ignore — already closing
                }
                fn();
            };

            const timeoutId = setTimeout(() => {
                settle(() => reject(new Error("Manual quality response timed out")));
            }, timeoutMs);

            client.on(MQTTEventsEnum.ERROR, (err) => {
                settle(() => reject(new Error(`MQTT error: ${err.message}`)));
            });

            client.on(MQTTEventsEnum.CLOSE, () => {
                if (!settled) {
                    settle(() => reject(new Error("MQTT closed before response")));
                }
            });

            client.on(MQTTEventsEnum.MESSAGE, (topic, message) => {
                if (topic !== responseTopic) {
                    return;
                }
                let response: IManualQualityResponsePayload;
                try {
                    response = JSON.parse(message.toString());
                } catch (err) {
                    return;
                }
                if (response.uuid !== payload.uuid) {
                    return;
                }
                settle(() => resolve(response));
            });

            let publishedOnce = false;
            client.on(MQTTEventsEnum.CONNECT, () => {
                if (publishedOnce) {
                    return;
                }
                publishedOnce = true;
                client.subscribe(responseTopic, { qos: 1 }, (subErr) => {
                    if (subErr) {
                        settle(() =>
                            reject(new Error(`Subscribe to response topic failed: ${subErr.message}`)),
                        );
                        return;
                    }
                    client.publish(
                        MANUAL_QUALITY_REQUEST_TOPIC,
                        JSON.stringify(payload),
                        { qos: 1 },
                        (pubErr) => {
                            if (pubErr) {
                                settle(() =>
                                    reject(new Error(`Publish failed: ${pubErr.message}`)),
                                );
                            }
                        },
                    );
                });
            });
        });
    },
};
